import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Download, Send, Umbrella, Stethoscope, User as UserIcon, PartyPopper, HeartCrack, Scale, Search, X } from "lucide-react";

const PTO_TYPES = [
  { key: "vacation",    field: "pto_vacation_hours",    label: "Vacation",    Icon: Umbrella },
  { key: "sick",        field: "pto_sick_hours",        label: "Sick",        Icon: Stethoscope },
  { key: "personal",    field: "pto_personal_hours",    label: "Personal",    Icon: UserIcon },
  { key: "holiday",     field: "pto_holiday_hours",     label: "Holiday",     Icon: PartyPopper },
  { key: "bereavement", field: "pto_bereavement_hours", label: "Bereavement", Icon: HeartCrack },
  { key: "jury",        field: "pto_jury_hours",        label: "Jury",        Icon: Scale },
];

export function AdminPaystubs() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/paystubs");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  // Unique employee names for the datalist autocomplete — makes searching fast
  // when a tenant has dozens of employees. We use a native <datalist> so the
  // browser handles the dropdown UI (no dependency).
  const employeeSuggestions = useMemo(() => {
    return Array.from(new Set(items.map((p) => p.employee_name).filter(Boolean))).sort();
  }, [items]);

  // Client-side filter: employee name (case-insensitive contains) + optional
  // date-range on the week_start / week_end fields. Runs against the loaded
  // set — since /paystubs returns the whole tenant this is instant and
  // doesn't need a backend query.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((p) => {
      if (q && !(p.employee_name || "").toLowerCase().includes(q)) return false;
      if (fromDate && p.week_end && p.week_end < fromDate) return false;
      if (toDate && p.week_start && p.week_start > toDate) return false;
      return true;
    });
  }, [items, search, fromDate, toDate]);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, p) => ({
        hours: acc.hours + Number(p.calc?.total_hours || 0),
        gross: acc.gross + Number(p.gross_pay ?? p.calc?.gross_pay ?? 0),
        net:   acc.net   + Number(p.net_pay || 0),
      }),
      { hours: 0, gross: 0, net: 0 },
    );
  }, [filtered]);

  const hasFilters = !!(search || fromDate || toDate);
  const clearFilters = () => { setSearch(""); setFromDate(""); setToDate(""); };

  // CSV export of the FILTERED view — useful for building an
  // employee-specific report for a custom timeframe (accountant, HR, IRS
  // audits, employee dispute review).
  const exportCsv = () => {
    if (filtered.length === 0) { toast.info("Nothing to export — adjust your filters"); return; }
    const rows = [
      ["Employee", "Week Start", "Week End", "Pay Date", "Hours", "Gross", "Federal Tax", "State Tax", "Local Tax", "Other Deductions", "Reimbursement", "Adjustment", "Tips", "Bonus", "PTO Pay", "Net Pay", "Status"],
      ...filtered.map((p) => {
        const d = p.deductions || {};
        return [
          p.employee_name || "",
          p.week_start || "",
          p.week_end || "",
          p.pay_date || "",
          Number(p.calc?.total_hours || 0).toFixed(2),
          Number(p.gross_pay ?? p.calc?.gross_pay ?? 0).toFixed(2),
          Number(d.federal_tax || 0).toFixed(2),
          Number(d.state_tax || 0).toFixed(2),
          Number(d.local_tax || 0).toFixed(2),
          Number(d.other_deduction || 0).toFixed(2),
          Number(d.reimbursement || 0).toFixed(2),
          Number(d.adjustment || 0).toFixed(2),
          Number(d.tips || 0).toFixed(2),
          Number(d.bonus || 0).toFixed(2),
          Number(d.pto_pay || 0).toFixed(2),
          Number(p.net_pay || 0).toFixed(2),
          p.status || "",
        ];
      }),
    ];
    // Basic CSV escaping — wrap in quotes, double any internal quotes
    const csv = rows.map((r) => r.map((cell) => {
      const s = String(cell ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    // Filename encodes the filter for at-a-glance context
    const emp = search ? `-${search.replace(/[^a-z0-9]+/gi, "_")}` : "";
    const range = (fromDate || toDate) ? `-${fromDate || "start"}_to_${toDate || "end"}` : "";
    a.href = url;
    a.download = `paystubs${emp}${range}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="admin-paystubs-page">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</p>
        <div className="mt-1 flex items-baseline justify-between gap-3 flex-wrap">
          <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900">Paystubs</h1>
          <p className="text-xs text-slate-500 tabular-nums" data-testid="paystubs-count-note">
            Showing <b className="text-slate-900">{filtered.length}</b> of {items.length} paystubs
            {hasFilters && <> · <button type="button" onClick={clearFilters} className="text-sky-600 hover:underline" data-testid="clear-filters-btn">clear filters</button></>}
          </p>
        </div>
      </header>

      <Card className="rounded-2xl border-slate-200" data-testid="paystubs-filter-panel">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr_1fr_auto] gap-3 items-end">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Employee</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="search"
                  autoComplete="off"
                  list="paystubs-employee-list"
                  placeholder="Search by name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="paystubs-employee-search"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md hover:bg-slate-100 flex items-center justify-center"
                    data-testid="paystubs-employee-search-clear"
                  >
                    <X className="h-3.5 w-3.5 text-slate-500" />
                  </button>
                )}
                <datalist id="paystubs-employee-list">
                  {employeeSuggestions.map((n) => <option key={n} value={n} />)}
                </datalist>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-600">From (pay period)</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1"
                data-testid="paystubs-from-date"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-600">To (pay period)</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1"
                data-testid="paystubs-to-date"
              />
            </div>
            <Button
              onClick={exportCsv}
              disabled={filtered.length === 0}
              variant="outline"
              className="h-10 shrink-0"
              data-testid="paystubs-export-csv-btn"
              title={filtered.length === 0 ? "Nothing matches your filters" : "Download filtered set as CSV"}
            >
              <Download className="h-4 w-4 mr-1.5" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Employee</th>
                <th className="text-left px-4 py-3 font-semibold">Period</th>
                <th className="text-right px-4 py-3 font-semibold">Hours</th>
                <th className="text-right px-4 py-3 font-semibold">Gross</th>
                <th className="text-right px-4 py-3 font-semibold">Net Pay</th>
                <th className="text-center px-4 py-3 font-semibold">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500" data-testid="paystubs-empty-state">
                    {items.length === 0
                      ? "No paystubs yet. Generate a pay run from Admin → Payroll."
                      : "No paystubs match your filters. Try a different name or date range."}
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100" data-testid={`paystub-row-${p.id}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">{p.employee_name}</td>
                    <td className="px-4 py-3 text-xs">{fmtDate(p.week_start)} – {fmtDate(p.week_end)}</td>
                    <td className="px-4 py-3 text-right">{p.calc.total_hours}h</td>
                    <td className="px-4 py-3 text-right">{fmtMoney(p.gross_pay ?? p.calc.gross_pay)}</td>
                    <td className="px-4 py-3 text-right text-lg font-extrabold text-green-700">{fmtMoney(p.net_pay)}</td>
                    <td className="px-4 py-3 text-center text-xs uppercase font-semibold">{p.status?.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/paystubs/${p.id}`)} data-testid={`edit-paystub-${p.id}-btn`}>Edit</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-slate-900 text-white" data-testid="paystubs-totals-row">
                <tr>
                  <td className="px-4 py-3 text-xs font-extrabold uppercase tracking-widest" colSpan={2}>
                    Report totals · {filtered.length} paystub{filtered.length !== 1 ? "s" : ""}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold" data-testid="totals-hours">{totals.hours.toFixed(2)}h</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold" data-testid="totals-gross">{fmtMoney(totals.gross)}</td>
                  <td className="px-4 py-3 text-right text-lg font-extrabold text-emerald-300 tabular-nums" data-testid="totals-net">{fmtMoney(totals.net)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export function AdminPaystubDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [benefits, setBenefits] = useState(null); // { benefits_enabled_globally, benefits: {...} }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/paystubs/${id}`);
    setData(data);
    const d = data.paystub.deductions || {};
    // pto_details is stored as [{benefit_type, hours, rate, pay}, ...]
    const priorPto = {};
    for (const row of (d.pto_details || [])) {
      priorPto[`pto_${row.benefit_type}_hours`] = Number(row.hours) || 0;
    }
    setForm({
      federal_tax: d.federal_tax || 0,
      state_tax: d.state_tax || 0,
      local_tax: d.local_tax || 0,
      other_deduction: d.other_deduction || 0,
      reimbursement: d.reimbursement || 0,
      adjustment: d.adjustment || 0,
      tips: d.tips || 0,
      tips_notes: d.tips_notes || "",
      bonus: d.bonus || 0,
      bonus_notes: d.bonus_notes || "",
      pto_vacation_hours:    priorPto.pto_vacation_hours    || 0,
      pto_sick_hours:        priorPto.pto_sick_hours        || 0,
      pto_personal_hours:    priorPto.pto_personal_hours    || 0,
      pto_holiday_hours:     priorPto.pto_holiday_hours     || 0,
      pto_bereavement_hours: priorPto.pto_bereavement_hours || 0,
      pto_jury_hours:        priorPto.pto_jury_hours        || 0,
      recompute_taxes: true,
      admin_notes: data.paystub.admin_notes || "",
    });
    // Load benefits config for this employee (only if benefits are enabled globally)
    try {
      const b = await api.get(`/admin/employees/${data.paystub.user_id}/benefits`);
      setBenefits(b.data);
    } catch {
      setBenefits({ benefits_enabled_globally: false, benefits: {} });
    }
  };
  useEffect(() => { load(); }, [id]);

  if (!data || !form) return <div className="py-20 text-center text-slate-500">Loading…</div>;
  const p = data.paystub;
  const cc = p.calc;

  const save = async () => {
    setBusy(true);
    try {
      const NUM_FIELDS = ["federal_tax","state_tax","local_tax","other_deduction","reimbursement","adjustment","tips","bonus","pto_vacation_hours","pto_sick_hours","pto_personal_hours","pto_holiday_hours","pto_bereavement_hours","pto_jury_hours"];
      const payload = {
        ...form,
        ...Object.fromEntries(NUM_FIELDS.map((k) => [k, Number(form[k]) || 0])),
        recompute_taxes: !!form.recompute_taxes,
      };
      await api.put(`/paystubs/${id}/deductions`, payload);
      toast.success("Saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const finalize = async () => {
    if (!confirm("Send paystub to employee for signature?")) return;
    setBusy(true);
    try {
      await api.post(`/paystubs/${id}/finalize`);
      toast.success("Sent for signature");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const downloadPdf = async () => {
    const resp = await api.get(`/reports/paystub-pdf/${id}`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url; a.download = `paystub_${p.employee_name}_${p.week_start}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const tipsNum = Number(form.tips) || 0;
  const bonusNum = Number(form.bonus) || 0;
  const hourlyRate = Number(cc.hourly_rate) || 0;
  const ptoHoursTotal = PTO_TYPES.reduce((sum, t) => sum + (Number(form[t.field]) || 0), 0);
  const ptoPay = Math.round(ptoHoursTotal * hourlyRate * 100) / 100;
  const effectiveGross = cc.gross_pay + tipsNum + bonusNum + ptoPay;
  const net =
    effectiveGross -
    (Number(form.federal_tax) || 0) -
    (Number(form.state_tax) || 0) -
    (Number(form.local_tax) || 0) -
    (Number(form.other_deduction) || 0) +
    (Number(form.reimbursement) || 0) +
    (Number(form.adjustment) || 0);

  return (
    <div className="space-y-5" data-testid="admin-paystub-detail">
      <Button variant="ghost" onClick={() => navigate(-1)} className="px-0"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{fmtDate(p.week_start)} – {fmtDate(p.week_end)}</p>
          <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">{p.employee_name}</h1>
          <p className="text-xs text-slate-500 uppercase tracking-wider">{p.worker_type} · {cc.pay_method_used}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadPdf} data-testid="paystub-pdf-btn"><Download className="h-4 w-4 mr-1" /> PDF</Button>
          {p.status === "draft" && (
            <Button onClick={finalize} className="bg-sky-600 hover:bg-sky-700" data-testid="paystub-finalize-btn">
              <Send className="h-4 w-4 mr-1" /> Send for Signature
            </Button>
          )}
        </div>
      </header>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-5 space-y-2">
            <h3 className="font-bold font-display mb-2">Hours & Production</h3>
            <Row label={`Regular — ${cc.regular_hours.toFixed(2)}h × ${fmtMoney(cc.hourly_rate)}`} value={fmtMoney(cc.regular_pay)} />
            {Number(cc.overtime_hours || 0) > 0 && (
              <Row label={`Overtime — ${cc.overtime_hours.toFixed(2)}h × ${fmtMoney(cc.overtime_rate)} (1.5× base)`} value={fmtMoney(cc.overtime_pay)} />
            )}
            <Row label="Hourly Total" value={fmtMoney(cc.hourly_total)} bold />
            {Number(cc.flsa_ot_premium || 0) > 0 && (
              <Row
                label={`FLSA OT premium — ${cc.overtime_hours.toFixed(2)}h × ½ × ${fmtMoney(cc.weekly_regular_rate || 0)}/hr`}
                value={fmtMoney(cc.flsa_ot_premium)}
              />
            )}
            <div className="h-2"></div>
            <Row label="Gross Production" value={fmtMoney(cc.gross_invoice_production)} />
            <Row label="Allowable Ded." value={fmtMoney(cc.allowable_deductions)} />
            <Row label="Net Commissionable" value={fmtMoney(cc.net_commissionable_revenue)} />
            <Row label="Commission %" value={`${cc.commission_percentage}%`} />
            <Row label="Commission Pay" value={fmtMoney(cc.commission_pay)} bold />
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-5 space-y-3">
            <h3 className="font-bold font-display mb-2">Earnings Additions <span className="text-xs font-normal text-slate-500 ml-1">(pre-tax)</span></h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Tips</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.tips}
                  onChange={(e) => setForm({ ...form, tips: e.target.value })}
                  disabled={p.status === "acknowledged"}
                  className="mt-1"
                  data-testid="paystub-tips-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Bonus</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.bonus}
                  onChange={(e) => setForm({ ...form, bonus: e.target.value })}
                  disabled={p.status === "acknowledged"}
                  className="mt-1"
                  data-testid="paystub-bonus-input"
                />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Tips Notes (optional)</Label>
                <Input
                  value={form.tips_notes || ""}
                  onChange={(e) => setForm({ ...form, tips_notes: e.target.value })}
                  disabled={p.status === "acknowledged"}
                  placeholder="e.g. Cash tips from Smith driveway job"
                  className="mt-1 text-xs"
                  data-testid="paystub-tips-notes-input"
                />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Bonus Notes (optional)</Label>
                <Input
                  value={form.bonus_notes || ""}
                  onChange={(e) => setForm({ ...form, bonus_notes: e.target.value })}
                  disabled={p.status === "acknowledged"}
                  placeholder="e.g. Perfect inspection week"
                  className="mt-1 text-xs"
                  data-testid="paystub-bonus-notes-input"
                />
              </div>
            </div>
            {(tipsNum + bonusNum + ptoPay) > 0 && (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="effective-gross-pill">
                Effective gross becomes <strong>{fmtMoney(effectiveGross)}</strong> — taxes recompute against this when &quot;Recompute taxes&quot; is on.
              </div>
            )}

            {benefits?.benefits_enabled_globally && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3" data-testid="paystub-pto-card">
                <h3 className="font-bold font-display text-sm text-sky-900 mb-1 flex items-center gap-2">
                  <Umbrella className="h-4 w-4" /> PTO Used This Period
                  <span className="text-[10px] font-normal text-sky-700 normal-case">
                    ({fmtMoney(hourlyRate)}/h · pre-tax)
                  </span>
                </h3>
                <p className="text-[11px] text-sky-800 mb-2">
                  Enter hours of PTO the employee is using this pay period. Each hour is paid at their base hourly rate and deducted from their balance when you save.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {PTO_TYPES.map(({ key, field, label, Icon }) => {
                    const cfg = benefits.benefits?.[key];
                    const enabled = !!cfg?.enabled;
                    const bal = Number(cfg?.balance_hours || 0);
                    const used = Number(form[field]) || 0;
                    const remaining = Math.round((bal - used) * 100) / 100;
                    const over = used > bal + 0.001;
                    return (
                      <div
                        key={key}
                        className={`rounded-lg border p-2 ${enabled ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-60"}`}
                        data-testid={`pto-block-${key}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-700 flex items-center gap-1">
                            <Icon className="h-3 w-3" /> {label}
                          </span>
                          <span className="text-[10px] text-slate-500 tabular-nums">
                            bal <b className={remaining < 0 ? "text-red-700" : "text-slate-800"}>{remaining.toFixed(2)}h</b>
                          </span>
                        </div>
                        {enabled ? (
                          <Input
                            type="number"
                            step="0.25"
                            min="0"
                            value={form[field]}
                            onChange={(ev) => setForm({ ...form, [field]: ev.target.value })}
                            disabled={p.status === "acknowledged"}
                            className={`h-8 text-sm ${over ? "border-red-400" : ""}`}
                            data-testid={`paystub-${field.replace(/_/g, "-")}-input`}
                            placeholder="0"
                          />
                        ) : (
                          <p className="text-[10px] text-slate-500 italic">Not enabled for this employee</p>
                        )}
                        {over && (
                          <p className="text-[10px] text-red-700 mt-1">Exceeds available balance</p>
                        )}
                      </div>
                    );
                  })}
                </div>
                {ptoPay > 0 && (
                  <div className="mt-2 text-[11px] text-sky-900 bg-sky-100 border border-sky-200 rounded-md px-2 py-1.5 tabular-nums" data-testid="pto-summary-total">
                    Total PTO pay: <b>{ptoHoursTotal.toFixed(2)}h × {fmtMoney(hourlyRate)}</b> = <b>{fmtMoney(ptoPay)}</b>
                  </div>
                )}
              </div>
            )}

            <h3 className="font-bold font-display pt-2 mb-1">Deductions &amp; Adjustments</h3>
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid="recompute-taxes-toggle">
              <input
                type="checkbox"
                checked={!!form.recompute_taxes}
                onChange={(e) => setForm({ ...form, recompute_taxes: e.target.checked })}
                disabled={p.status === "acknowledged" || !p.taxes_enabled}
                className="h-4 w-4"
              />
              <span>Auto-recompute taxes from company % when I save{!p.taxes_enabled && " (auto-tax was OFF when this paystub was generated)"}</span>
            </label>
            {["federal_tax","state_tax","local_tax","other_deduction","reimbursement","adjustment"].map((k) => (
              <div key={k}>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">{k.replace(/_/g, " ")}</Label>
                <Input type="number" step="0.01" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} disabled={p.status === "acknowledged"} className="mt-1" data-testid={`deduction-${k}-input`} />
              </div>
            ))}

            {/* Itemized reimbursement breakdown — sourced from approved
                lead-tech reimbursement requests that landed on this paystub. */}
            {(p.deductions?.reimbursement_details || []).length > 0 && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-3" data-testid="paystub-reimb-breakdown">
                <div className="text-xs font-bold uppercase tracking-wider text-sky-900 mb-2">
                  Reimbursements (post-tax)
                </div>
                <ul className="space-y-1 text-sm">
                  {(p.deductions.reimbursement_details || []).map((r, i) => (
                    <li key={r.id || i} className="flex justify-between gap-2">
                      <span className="text-slate-700 truncate">
                        {r.store_name}
                        <span className="ml-2 text-xs text-slate-500 uppercase tracking-wider">{r.reason}</span>
                      </span>
                      <span className="font-semibold text-emerald-700">+${Number(r.amount || 0).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Admin Notes</Label>
              <Textarea value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} disabled={p.status === "acknowledged"} className="mt-1" />
            </div>
            <Button onClick={save} disabled={busy || p.status === "acknowledged"} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="save-deductions-btn">Save</Button>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl bg-slate-900 text-white">
        <CardContent className="p-5 flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400" data-testid="effective-gross-label">
              Gross {fmtMoney(effectiveGross)}
              {(tipsNum + bonusNum + ptoPay) > 0 && (
                <span className="ml-2 text-emerald-300 normal-case tracking-normal">
                  (base {fmtMoney(cc.gross_pay)}
                  {(tipsNum + bonusNum) > 0 && ` + tips/bonus ${fmtMoney(tipsNum + bonusNum)}`}
                  {ptoPay > 0 && ` + PTO ${fmtMoney(ptoPay)}`}
                  )
                </span>
              )}
            </p>
            <p className="text-sm text-slate-300">After deductions / adjustments</p>
          </div>
          <p className="text-4xl font-extrabold font-display text-green-400" data-testid="effective-net-pay">{fmtMoney(net)}</p>
        </CardContent>
      </Card>

      {data.signature && (
        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-5">
            <h3 className="font-bold font-display mb-3">Employee Acknowledgment</h3>
            <img src={data.signature.signature_data} alt="sig" className="bg-white border border-slate-200 rounded-xl max-h-32" />
            <p className="text-xs text-slate-500 mt-2">Signed: {p.acknowledged_at}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={bold ? "font-bold text-slate-900" : "font-medium text-slate-900"}>{value}</span>
    </div>
  );
}
